import { useEffect, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { supabase } from '../../api/supabase'
import { useAuth } from '../../contexts/AuthContext'
import { hasAnyRole } from '../../lib/roles'
import GroupDashboard from '../../components/Group/GroupDashboard'
import ChargesTab from '../../components/JobTabs/ChargesTab'
import TransportTab from '../../components/JobTabs/TransportTab'
import { fetchGroupRows, type GroupRow } from '../../lib/groupRows'

// A group job (job_type GROUP) is its own page, not the ten-tab job page: a Dashboard of its shipments (each a real job, shown as one
// row) and the group's Charges. Charges and the invoice live here only; the shipments are not billed. No customs on a group.
type GroupTab = 'dashboard' | 'charges' | 'notes'
const TABS: { id: GroupTab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard' },
  { id: 'charges', label: 'Charges' },
  { id: 'notes', label: 'Collection / Delivery Note' },
]

// The notes tab is the Transport tab's table in group mode: it needs the group's current shipment rows (to pick one, and to carry its data over).
function GroupNotes({ group }: { group: { id: string; job_ref: string } }) {
  const [rows, setRows] = useState<GroupRow[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  useEffect(() => { fetchGroupRows(group.id).then(setRows).catch(e => setError(e instanceof Error ? e.message : 'Could not load the shipments')) }, [group.id])
  if (error) return <p className="text-sm text-red-600">{error}</p>
  if (!rows) return <p className="text-sm text-gray-400 py-6 text-center">Loading…</p>
  return <TransportTab jobId={group.id} jobData={{ job_ref: group.job_ref }} groupRows={rows} />
}

export default function GroupPage({ jobData }: { jobData: { id: string; job_ref: string; invoice_to_id: string | null; status: string | null } }) {
  const { userRoles } = useAuth()
  const [params, setParams] = useSearchParams()
  const editable = hasAnyRole(userRoles, ['ADMIN', 'OPERATIONS'])
  const rowParam = params.get('row')
  const tab = (params.get('tab') as GroupTab | null) ?? 'dashboard'
  const [customerName, setCustomerName] = useState<string | null>(null)

  useEffect(() => {
    if (!jobData.invoice_to_id) { setCustomerName(null); return }
    supabase.from('customers').select('name').eq('id', jobData.invoice_to_id).maybeSingle()
      .then(({ data }) => setCustomerName((data as { name: string } | null)?.name ?? null))
  }, [jobData.invoice_to_id])

  const setTab = (t: GroupTab) => setParams(p => { const n = new URLSearchParams(p); n.set('tab', t); n.delete('row'); return n }, { replace: true })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-[1500px] mx-auto px-4 py-6">
        <Link to="/jobs" className="text-sm text-blue-600 hover:underline dark:text-blue-400">← Jobs</Link>
        <div className="mt-2 mb-4 flex flex-wrap items-baseline gap-3">
          <h1 className="text-2xl font-bold font-mono text-gray-900 dark:text-white">{jobData.job_ref}</h1>
          <span className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300 font-medium">Group</span>
          <span className="text-sm text-gray-600 dark:text-gray-300">{customerName ? `Customer: ${customerName}` : <span className="text-amber-600">No customer set</span>}</span>
          {!editable && <span className="text-xs text-gray-500">view only</span>}
        </div>

        <div className="flex gap-1 border-b border-gray-200 dark:border-gray-700 mb-4">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`px-5 py-3 text-sm font-medium -mb-px border-b-2 ${tab === t.id ? 'border-blue-600 text-blue-600 dark:text-blue-400' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
              {t.label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' && <GroupDashboard groupId={jobData.id} groupRef={jobData.job_ref} customerName={customerName} editable={editable} highlightJobId={rowParam} onOpenNotes={() => setTab('notes')} />}
        {tab === 'notes' && <GroupNotes group={jobData} />}
        {tab === 'charges' && <ChargesTab jobId={jobData.id} defaultBillToId={jobData.invoice_to_id} />}
      </div>
    </div>
  )
}
